import React, { useMemo, useState } from 'react';
import { useGetComplaintsGraphQuery } from '../../redux/complaintApi';
import { SearchableSelect } from './SearchableSelect';

const PERIOD_FILTER_OPTIONS = [
  { label: 'This Month', value: 'this_month', apiFilter: 'this_month' },
  { label: 'Last Month', value: 'last_month', apiFilter: 'last_month' },
  { label: 'Last 3 Months', value: 'last_3_months', apiFilter: 'last_3_months' },
];

const LEGEND_ITEMS = [
  { key: 'resolved', color: 'bg-emerald-600', label: 'Resolved Complaints', mobileLabel: 'Resolved' },
  { key: 'inprogress', color: 'bg-red-600', label: 'In-Progress', mobileLabel: 'In Progress' },
  { key: 'overdue', color: 'bg-orange-600', label: 'Overdue Complaints', mobileLabel: 'Overdue' },
];

const filterSelectClasses =
  'w-full rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-xs font-medium text-gray-800 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const normalizeGraph = (payload) => {
  const raw = payload?.data ?? payload ?? {};

  const dateWiseList = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.data)
      ? raw.data
      : null;

  if (dateWiseList) {
    const rows = dateWiseList
      .filter((r) => r && typeof r === 'object')
      .map((r) => ({
        date: String(r.date ?? r.day ?? r.label ?? ''),
        resolved: toNum(r.resolved ?? r.resolved_complaints),
        inprogress: toNum(
          r.inprogress ??
            r.inprogress_complaints ??
            r.work_in_progress ??
            r.work_in_progress_complaints
        ),
        overdue: toNum(r.overdue ?? r.overdue_complaints),
      }))
      .filter((r) => r.date);

    return {
      dates: rows.map((r) => r.date),
      resolved: rows.map((r) => r.resolved),
      inprogress: rows.map((r) => r.inprogress),
      overdue: rows.map((r) => r.overdue),
      days: rows.length,
    };
  }

  const seriesObj = raw?.series && !Array.isArray(raw.series) ? raw.series : null;
  const base = seriesObj || raw;

  const byKey = (keys) => {
    for (const k of keys) {
      const v = base?.[k];
      if (Array.isArray(v)) return v.map(toNum);
    }
    return null;
  };

  let resolved =
    byKey(['resolved', 'resolved_complaints', 'resolvedCounts', 'resolved_count']) || [];
  let inprogress =
    byKey([
      'inprogress',
      'inprogress_complaints',
      'work_in_progress',
      'work_in_progress_complaints',
    ]) || [];
  let overdue = byKey(['overdue', 'overdue_complaints']) || [];

  const list = Array.isArray(raw?.series) ? raw.series : Array.isArray(raw?.data) ? raw.data : null;
  if (list && (!resolved.length || !inprogress.length || !overdue.length)) {
    for (const s of list) {
      const name = String(s?.name ?? s?.status ?? s?.label ?? '').toLowerCase();
      const data = Array.isArray(s?.data) ? s.data.map(toNum) : null;
      if (!data) continue;
      if (name.includes('resolv')) resolved = data;
      else if (name.includes('progress') || name.includes('inprogress')) inprogress = data;
      else if (name.includes('overdue')) overdue = data;
    }
  }

  const days = Math.max(resolved.length, inprogress.length, overdue.length, 0);
  const padToDays = (arr) => {
    const out = Array.isArray(arr) ? arr.slice(0, days) : [];
    while (out.length < days) out.push(0);
    return out;
  };

  return {
    dates: Array.from({ length: days }, (_, i) => String(i + 1)),
    resolved: padToDays(resolved),
    inprogress: padToDays(inprogress),
    overdue: padToDays(overdue),
    days,
  };
};

function buildTickLabels(dates, hasData, maxTicks = 5) {
  const len = hasData ? dates.length : 0;
  if (len === 0) return [''];
  const step = len <= maxTicks ? 1 : Math.ceil(len / maxTicks);
  const out = [];
  for (let i = 0; i < len; i += step) {
    const d = dates[i];
    out.push(d?.slice(5) || d);
  }
  const last = dates[len - 1];
  const lastLabel = last?.slice(5) || last;
  if (out[out.length - 1] !== lastLabel) out.push(lastLabel);
  return out;
}

function ComplaintsChart() {
  const [periodFilter, setPeriodFilter] = useState('this_month');

  const apiFilter = useMemo(() => {
    const opt = PERIOD_FILTER_OPTIONS.find((o) => o.value === periodFilter);
    return opt?.apiFilter || 'this_month';
  }, [periodFilter]);

  const { data, isLoading, isFetching } = useGetComplaintsGraphQuery(apiFilter, {
    refetchOnMountOrArgChange: true,
  });
  const graph = useMemo(() => normalizeGraph(data), [data]);

  const days = graph.days || 0;
  const hasData = days > 0;
  const viewDays = hasData ? days : 30;
  const maxVal = Math.max(...graph.resolved, ...graph.inprogress, ...graph.overdue, 1);

  const tickLabels = useMemo(
    () => buildTickLabels(graph.dates, hasData, 5),
    [graph.dates, hasData]
  );

  const chartHeight = 100;
  const chartTop = 10;

  const toPoints = (values) =>
    values.map((v, i) => `${i * 4},${chartTop + chartHeight - (v / maxVal) * chartHeight}`).join(' ');

  return (
    <div className="rounded-xl bg-white p-4 shadow-sm sm:p-6">
      <div className="mb-4 flex flex-col gap-3 border-b border-gray-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-base font-semibold text-gray-900">Register Complaints</h3>
        <div className="w-full sm:w-36">
          <SearchableSelect
            name="periodFilter"
            value={periodFilter}
            onChange={(e) => setPeriodFilter(e.target.value)}
            options={PERIOD_FILTER_OPTIONS.map(({ label, value }) => ({ label, value }))}
            placeholder="This Month"
            clearable={false}
            className={filterSelectClasses}
          />
        </div>
      </div>

      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="grid flex-1 grid-cols-3 gap-1.5 sm:gap-3">
          {LEGEND_ITEMS.map((item) => (
            <span
              key={item.key}
              className="inline-flex min-w-0 items-center gap-1 rounded-md bg-[#faf8f5] px-1.5 py-1 text-[10px] text-gray-600 sm:gap-1.5 sm:bg-transparent sm:px-0 sm:py-0 sm:text-xs"
            >
              <span className={`h-2 w-2 shrink-0 rounded-full sm:h-2.5 sm:w-2.5 ${item.color}`} />
              <span className="truncate sm:hidden">{item.mobileLabel}</span>
              <span className="hidden truncate sm:inline">{item.label}</span>
            </span>
          ))}
        </div>
        {(isLoading || isFetching) ? (
          <span className="shrink-0 text-[10px] text-gray-400">Loading…</span>
        ) : null}
      </div>

      <div className="rounded-lg bg-[#faf8f5]/60 p-2 sm:bg-transparent sm:p-0">
        <div className="h-44 sm:h-52">
          {hasData ? (
            <svg
              viewBox={`0 0 ${viewDays * 4} ${chartTop + chartHeight + 10}`}
              preserveAspectRatio="none"
              className="h-full w-full"
              role="img"
              aria-label="Complaints trend chart"
            >
              {[0.25, 0.5, 0.75].map((pct) => (
                <line
                  key={pct}
                  x1="0"
                  y1={chartTop + chartHeight * (1 - pct)}
                  x2={viewDays * 4}
                  y2={chartTop + chartHeight * (1 - pct)}
                  stroke="#e8e4df"
                  strokeWidth="0.5"
                />
              ))}
              <polyline fill="none" stroke="#2e7d32" strokeWidth="1.5" points={toPoints(graph.resolved)} />
              <polyline fill="none" stroke="#c62828" strokeWidth="1.5" points={toPoints(graph.inprogress)} />
              <polyline fill="none" stroke="#e65100" strokeWidth="1.5" points={toPoints(graph.overdue)} />
            </svg>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-gray-400">
              {isLoading ? 'Loading chart…' : 'No data for this period'}
            </div>
          )}
        </div>
        <div className="mt-2 flex justify-between gap-1 text-[9px] text-gray-500 sm:text-[10px]">
          {hasData ? (
            tickLabels.map((t, idx) => (
              <span key={`${t}-${idx}`} className="min-w-0 truncate text-center">
                {t}
              </span>
            ))
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default ComplaintsChart;
